import { type RefObject, useRef, useEffect, useCallback } from 'react'

// 全局曝光记录，确保每个元素在整个页面生命周期中只曝光一次
const exposedElementsSet = new Set<string>()

export interface UseExposureDetectionOptions {
  /**
   * 唯一标识，用于判断是否已曝光
   * 如果不提供，将使用元素的唯一标识（如果可能）
   */
  exposureKey?: string
  /**
   * 可见性阈值，0-1 之间，默认 0.5（50%）
   */
  threshold?: number
  /**
   * 停留时间（毫秒），默认 2000ms
   */
  duration?: number
  /**
   * 是否只触发一次，默认 true
   */
  once?: boolean
  /**
   * 曝光回调函数
   */
  onExposure?: () => void
  /**
   * IntersectionObserver 的 rootMargin
   */
  rootMargin?: string
}

/**
 * 曝光检测 Hook
 * 使用 IntersectionObserver 检测元素是否至少 50% 可见且停留时间超过指定时间
 * 确保在整个页面生命周期中只曝光一次
 *
 * @param elementRef - 要检测的元素的 ref
 * @param options - 配置选项
 * @returns 返回清除计时器的方法（用于切换时清理）
 *
 * @example
 * ```tsx
 * const MyComponent = () => {
 *   const ref = useRef<HTMLDivElement>(null)
 *   const { clearTimer } = useExposureDetection(ref, {
 *     exposureKey: 'my-component-1',
 *     threshold: 0.5,
 *     duration: 2000,
 *     onExposure: () => {
 *       console.log('元素已曝光')
 *     }
 *   })
 *
 *   return <div ref={ref}>内容</div>
 * }
 * ```
 */
export const useExposureDetection = (elementRef: RefObject<HTMLElement>, options: UseExposureDetectionOptions = {}) => {
  const { exposureKey, threshold = 0.5, duration = 2000, once = true, onExposure, rootMargin = '0px' } = options

  const exposureTimerRef = useRef<number | null>(null)
  const generatedKeyRef = useRef<string | null>(null)

  // 生成唯一的曝光 key
  const getExposureKey = useCallback(() => {
    if (exposureKey) {
      return exposureKey
    }
    // 如果没有提供 key，尝试使用元素的唯一标识
    if (elementRef.current && !generatedKeyRef.current) {
      // 使用元素的 data-exposure-id 属性，或者生成一个基于位置的 key
      const dataId = elementRef.current.getAttribute('data-exposure-id')
      if (dataId) {
        generatedKeyRef.current = dataId
        return dataId
      }
      // 如果没有 data-exposure-id，使用元素在 DOM 中的位置生成 key
      const path: string[] = []
      let el: Element | null = elementRef.current
      while (el && el !== document.body) {
        const index = Array.from(el.parentElement?.children || []).indexOf(el)
        path.unshift(`${el.tagName.toLowerCase()}[${index}]`)
        el = el.parentElement
      }
      generatedKeyRef.current = path.join(' > ')
      return generatedKeyRef.current
    }
    return generatedKeyRef.current || ''
  }, [exposureKey, elementRef])

  // 清除计时器的方法（不重置曝光状态）
  const clearTimer = useCallback(() => {
    if (exposureTimerRef.current) {
      clearTimeout(exposureTimerRef.current)
      exposureTimerRef.current = null
    }
  }, [])

  useEffect(() => {
    const element = elementRef.current
    if (!element || !onExposure) return

    const key = getExposureKey()
    if (!key) {
      console.warn('useExposureDetection: 无法生成唯一的 exposureKey，请提供 exposureKey 参数')
      return
    }

    // 如果已经曝光过且设置了 once，直接返回，不再监听
    if (once && exposedElementsSet.has(key)) {
      return
    }

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          // 如果可见性达到阈值（默认 50%）
          if (entry.intersectionRatio >= threshold) {
            // 如果还没有触发过曝光且没有正在计时的计时器，开始计时
            if ((!once || !exposedElementsSet.has(key)) && !exposureTimerRef.current) {
              exposureTimerRef.current = window.setTimeout(() => {
                // 再次检查是否已曝光（防止在计时期间被其他操作触发）
                if (once && exposedElementsSet.has(key)) return

                // 标记为已曝光
                if (once) {
                  exposedElementsSet.add(key)
                }
                // 触发曝光事件
                onExposure()
              }, duration)
            }
          } else {
            // 如果可见性低于阈值，清除计时器（但不移除曝光标记）
            if (exposureTimerRef.current) {
              clearTimeout(exposureTimerRef.current)
              exposureTimerRef.current = null
            }
          }
        })
      },
      {
        threshold: threshold,
        rootMargin: rootMargin,
      }
    )

    observer.observe(element)

    return () => {
      observer.disconnect()
      if (exposureTimerRef.current) {
        clearTimeout(exposureTimerRef.current)
        exposureTimerRef.current = null
      }
    }
  }, [elementRef, onExposure, threshold, duration, once, rootMargin, getExposureKey])

  return { clearTimer }
}

/**
 * 清除指定 key 的曝光记录（用于测试或特殊场景）
 */
export const clearExposureRecord = (key: string) => {
  exposedElementsSet.delete(key)
}

/**
 * 清除所有曝光记录（用于测试或特殊场景）
 */
export const clearAllExposureRecords = () => {
  exposedElementsSet.clear()
}

/**
 * 检查指定 key 是否已曝光
 */
export const hasExposed = (key: string): boolean => {
  return exposedElementsSet.has(key)
}
